/**
 * Sanitization carried forward from the legacy useAgentChat path.
 *
 * Raw model output and persisted drafts can contain internal UUIDs. We strip
 * them from anything that reaches the UI. This is applied at exactly three
 * boundaries: the `data-draft` stream handler, `ConversationStore.load`, and
 * the markdown render boundary (`StreamdownMessage`).
 */

import type { AiDraft } from '@/shared/types/chat';

const UUID_REGEX = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;

export function sanitizeUiText(value: string): string {
  return value.replace(UUID_REGEX, '[hidden-id]');
}

function buildDeterministicChangeLabel(payload: Record<string, unknown>): string {
  const operation =
    typeof payload.operation === 'string' ? payload.operation.toLowerCase() : 'update';
  const entity = typeof payload.entity === 'string' ? payload.entity.toLowerCase() : 'item';

  const operationLabel = operation.charAt(0).toUpperCase() + operation.slice(1);
  const entityLabel = entity.charAt(0).toUpperCase() + entity.slice(1);
  return `${operationLabel} ${entityLabel}`;
}

/**
 * Normalizes a draft into a safe, fully-populated shape and strips UUIDs from
 * every user-visible field. Ported verbatim from useAgentChat.sanitizeDraft.
 */
export function sanitizeDraft(draft: AiDraft): AiDraft {
  const sanitizedChanges = Array.isArray(draft.changes)
    ? draft.changes.map((change) => {
        const safePayload: Record<string, unknown> =
          change.payload && typeof change.payload === 'object' ? change.payload : {};
        const changeId =
          typeof change.changeId === 'string' && change.changeId.trim().length > 0
            ? change.changeId
            : crypto.randomUUID();
        const targetLabel =
          typeof change.targetLabel === 'string' && change.targetLabel.trim().length > 0
            ? change.targetLabel
            : 'Item';
        const label =
          typeof change.label === 'string' && change.label.trim().length > 0
            ? change.label
            : buildDeterministicChangeLabel(safePayload);

        return {
          changeId,
          label: sanitizeUiText(label),
          targetLabel: sanitizeUiText(targetLabel),
          destructive: Boolean(change.destructive),
          preview: Array.isArray(change.preview)
            ? change.preview.map((entry) => ({
                label: sanitizeUiText(String(entry.label ?? 'detail')),
                before: typeof entry.before === 'string' ? sanitizeUiText(entry.before) : undefined,
                after: typeof entry.after === 'string' ? sanitizeUiText(entry.after) : undefined,
              }))
            : [],
          payload: safePayload,
        };
      })
    : [];

  return {
    draftId:
      typeof draft.draftId === 'string' && draft.draftId.trim().length > 0
        ? draft.draftId
        : crypto.randomUUID(),
    changes: sanitizedChanges,
    hasDestructive: sanitizedChanges.some((change) => change.destructive),
  };
}
